import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function wrapHeroTitleRows(hero: HTMLElement): HTMLElement[] {
    const rows = Array.from(hero.querySelectorAll<HTMLElement>('.hero-landing__title-row'));

    return rows.map(row => {
        const existing = row.querySelector<HTMLElement>('.hero-landing__title-inner');
        if (existing) return existing;

        const inner = document.createElement('span');
        inner.className = 'hero-landing__title-inner';

        while (row.firstChild) {
            inner.appendChild(row.firstChild);
        }

        row.appendChild(inner);
        return inner;
    });
}

function settleHero(hero: HTMLElement): void {
    hero.classList.remove('is-animating');
    hero.classList.add('is-ready');

    const entry = hero.querySelector<HTMLElement>('[data-hero-entry]');
    const entryLines = Array.from(hero.querySelectorAll<HTMLElement>('[data-entry-line]'));
    const alwaysVisible = [
        ...Array.from(hero.querySelectorAll<HTMLElement>('[data-hero-copy]')),
        ...Array.from(hero.querySelectorAll<HTMLElement>('.hero-landing__title-inner')),
        ...Array.from(hero.querySelectorAll<HTMLElement>('.hero-landing__visual .sys-node')),
        ...Array.from(hero.querySelectorAll<HTMLElement>('.hero-landing__grid'))
    ];

    gsap.killTweensOf(alwaysVisible);
    gsap.set(alwaysVisible, { autoAlpha: 1, x: 0, y: 0, yPercent: 0, scale: 1, z: 0, rotate: 0, filter: 'blur(0px)' });
    gsap.killTweensOf([entry, ...entryLines].filter(Boolean) as HTMLElement[]);
    if (entry) gsap.set(entry, { autoAlpha: 0, pointerEvents: 'none' });
    gsap.set(entryLines, { autoAlpha: 0 });
}

function initHeroPointerDepth(hero: HTMLElement): void {
    if (prefersReducedMotion() || window.matchMedia('(pointer: coarse)').matches) return;

    const mesh = hero.querySelector<HTMLElement>('.hero-landing__visual .arch-mesh__container');
    const grid = hero.querySelector<HTMLElement>('.hero-landing__grid');
    if (!mesh && !grid) return;

    const meshX = mesh ? gsap.quickTo(mesh, 'x', { duration: 1.2, ease: 'power3.out' }) : null;
    const meshY = mesh ? gsap.quickTo(mesh, 'y', { duration: 1.2, ease: 'power3.out' }) : null;
    const gridX = grid ? gsap.quickTo(grid, 'x', { duration: 1.4, ease: 'power3.out' }) : null;
    const gridY = grid ? gsap.quickTo(grid, 'y', { duration: 1.4, ease: 'power3.out' }) : null;

    hero.addEventListener('pointermove', event => {
        const rect = hero.getBoundingClientRect();
        const nx = (event.clientX - rect.left) / rect.width - 0.5;
        const ny = (event.clientY - rect.top) / rect.height - 0.5;

        meshX?.(-nx * 18);
        meshY?.(-ny * 14);
        gridX?.(-nx * 8);
        gridY?.(-ny * 6);
    });
}

function initHeroScrollDrift(hero: HTMLElement): void {
    if (prefersReducedMotion()) return;

    const visual = hero.querySelector<HTMLElement>('[data-hero-mesh]');
    const grid = hero.querySelector<HTMLElement>('[data-hero-grid]');

    if (visual) {
        gsap.to(visual, {
            y: 120,
            opacity: 0.18,
            ease: 'none',
            scrollTrigger: {
                trigger: hero,
                start: 'top top',
                end: 'bottom top',
                scrub: true
            }
        });
    }

    if (grid) {
        gsap.to(grid, {
            y: 60,
            ease: 'none',
            scrollTrigger: {
                trigger: hero,
                start: 'top top',
                end: 'bottom top',
                scrub: true
            }
        });
    }
}

export function initCinematicHero(): void {
    const hero = document.querySelector<HTMLElement>('[data-cinematic-hero]');
    if (!hero) return;

    const titleRows = wrapHeroTitleRows(hero);
    const entry = hero.querySelector<HTMLElement>('[data-hero-entry]');
    const entryLines = Array.from(hero.querySelectorAll<HTMLElement>('[data-entry-line]'));
    const copyItems = Array.from(hero.querySelectorAll<HTMLElement>('[data-hero-copy]'));
    const visual = hero.querySelector<HTMLElement>('[data-hero-mesh]');
    const grid = hero.querySelector<HTMLElement>('[data-hero-grid]');
    const nodes = Array.from(hero.querySelectorAll<HTMLElement>('.hero-landing__visual .sys-node'));
    const scan = hero.querySelector<HTMLElement>('.hero-landing__entry-scan');

    initHeroPointerDepth(hero);
    initHeroScrollDrift(hero);

    if (prefersReducedMotion()) {
        settleHero(hero);
        return;
    }

    hero.classList.add('is-animating');

    if (entry) gsap.set(entry, { autoAlpha: 1, scale: 1, filter: 'blur(0px)' });
    gsap.set(entryLines, { autoAlpha: 0, y: 42, scale: 0.9, filter: 'blur(12px)' });
    gsap.set(titleRows, { yPercent: 112, scale: 0.98, filter: 'blur(8px)' });
    gsap.set(copyItems, { autoAlpha: 0, y: 28, filter: 'blur(10px)' });
    gsap.set(nodes, {
        autoAlpha: 0,
        scale: 0.54,
        z: -180,
        transformPerspective: 1100
    });
    if (visual) gsap.set(visual, { autoAlpha: 0, scale: 1.12, y: 18, transformPerspective: 1200 });
    if (grid) gsap.set(grid, { autoAlpha: 0, scale: 1.08 });
    if (scan) gsap.set(scan, { scaleX: 0, autoAlpha: 0, transformOrigin: 'left center' });

    let settled = false;
    const safeSettle = (): void => {
        if (settled) return;
        settled = true;
        settleHero(hero);
    };

    const timeline = gsap.timeline({
        defaults: { ease: 'power4.out' },
        onComplete: safeSettle
    });

    if (grid) {
        timeline.to(grid, { autoAlpha: 1, scale: 1, duration: 1.05, ease: 'power2.out' }, 0);
    }

    if (visual) timeline.to(visual, { autoAlpha: 0.64, scale: 1, y: 0, duration: 1.28, ease: 'power3.out' }, 0.04);

    timeline
        .to(entryLines, { autoAlpha: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.62, stagger: 0.055 }, 0.06)
        .fromTo(scan, { scaleX: 0, autoAlpha: 0 }, { scaleX: 1, autoAlpha: 1, duration: 0.36, ease: 'power2.out' }, 0.52)
        .to(nodes, {
            autoAlpha: 1,
            scale: 1,
            z: 0,
            duration: 1.04,
            stagger: { amount: 0.84, from: 'center' },
            ease: 'power3.out'
        }, 0.2)
        .to(entryLines, { autoAlpha: 0, y: -28, scale: 1.035, filter: 'blur(9px)', duration: 0.44, stagger: 0.025, ease: 'power3.in' }, 0.96)
        .to(scan, { scaleX: 0, autoAlpha: 0, duration: 0.38, ease: 'power2.in', transformOrigin: 'right center' }, 1.08)
        .to(titleRows, { yPercent: 0, scale: 1, filter: 'blur(0px)', duration: 0.96, stagger: 0.11 }, 1.16)
        .to(copyItems, { autoAlpha: 1, y: 0, filter: 'blur(0px)', duration: 0.78, stagger: 0.075, ease: 'power3.out' }, 1.48)
        .to(hero, { '--hero-cinematic-glow': 1, duration: 1.12, ease: 'power2.out' }, 0.86);

    window.setTimeout(safeSettle, 3400);
}
